import { sx } from '../../ui/designStyles';
import { usePerf } from './ctx';
import { COLS, ord, pc } from './lib';
import { BenchFrame, benchRows, ownRow, setBenchUi, useBenchUi, type BenchRow } from './portfolioHead';

const pick = (arr: number[], q: number) => {
  if (!arr.length) return null;
  const s = arr.slice().sort((x, y) => x - y);
  return s[Math.min(s.length - 1, Math.floor(q * (s.length - 1) + 0.5))];
};
const tone = (v: number) => (v >= 50 ? '#1F6B52' : v >= 25 ? '#5B6B7C' : '#B3261E');
const dagger = (ci: number) => (ci >= 7 && ci <= 12 ? ' †' : '');

/**
 * Distribution (design dx, L44883-44956; T16561-16625): the sixteen metrics on one
 * percentile axis, one dot per building, the spread, the skew, the biggest drag, and the
 * buildings ranked on the chosen metric. A Facility Admin sees their own building on the
 * peer band only; other facilities are never named here.
 */
export function DistributionTab() {
  const ctx = usePerf();
  const u = useBenchUi();
  const own = ownRow(ctx);
  const dsc: BenchRow[] = ctx.op ? benchRows(ctx, u).shown : own ? [own] : [];
  const one = !ctx.op || dsc.length < 3;
  const openB = (id: string) => () => {
    if (!ctx.op) return;
    setBenchUi({ openRow: id });
    ctx.goTab('portfolio');
  };
  const stats = COLS.map((c, ci) => {
    const vals = dsc.map((r) => ({ r, c: r.cells[ci] })).filter((x): x is { r: BenchRow; c: number } => x.c !== null);
    const nums = vals.map((x) => x.c);
    return {
      label: c.label,
      ci,
      vals,
      p10: one ? 10 : pick(nums, 0.1),
      p50: one ? 50 : pick(nums, 0.5),
      p90: one ? 90 : pick(nums, 0.9),
      mean: nums.length ? Math.round(nums.reduce((a, x) => a + x, 0) / nums.length) : null,
      low: vals.slice().sort((a, b) => a.c - b.c)[0] ?? null,
    };
  });
  const def = stats.filter((x) => x.vals.length).sort((a, b) => (a.p50 ?? 0) - (b.p50 ?? 0) || a.vals[0].c - b.vals[0].c)[0];
  const selI = u.distMetric ?? (def ? def.ci : 0);
  const sel = stats[selI] ?? stats[0];
  const peerLabel = ctx.peerLabel;

  const rows = stats.map((st) => {
    const on = st.ci === selI;
    const seen: Record<number, number> = {};
    const thin = st.vals.length < (one ? 1 : 3);
    const skewN = !one && !thin && st.mean !== null && st.p50 !== null && Math.abs(st.mean - st.p50) >= 1 ? st.mean - st.p50 : 0;
    return {
      st,
      on,
      thin,
      skewN,
      dots: st.vals.map((x) => {
        const b = Math.round(x.c / 2);
        seen[b] = (seen[b] ?? 0) + 1;
        const tier = seen[b] - 1;
        const pinned = u.openRow === x.r.id;
        const sz = pinned ? 12 : 9;
        return {
          key: x.r.id,
          onClick: openB(x.r.id),
          tip: x.r.name + ' · ' + st.label + ' ' + ord(x.c) + (ctx.op ? ' · click to open' : ''),
          style:
            'position:absolute;cursor:pointer;left:' + Math.max(0, Math.min(100, x.c)) + '%;top:' + (10 - sz / 2 + (tier ? (tier % 2 ? -5 : 5) : 0)) + 'px;width:' + sz + 'px;height:' + sz + 'px;margin-left:' + -sz / 2 +
            'px;border-radius:50%;background:' + (pinned ? '#22272E' : pc(x.c)) + ';box-shadow:0 0 0 1.5px #FCFAF4;transition:transform .15s;',
        };
      }),
    };
  });
  const rank = one
    ? stats
        .filter((x) => x.vals.length)
        .sort((a, b) => a.vals[0].c - b.vals[0].c)
        .map((x, i) => ({ n: String(i + 1), key: x.label, name: x.label, v: ord(x.vals[0].c), onClick: () => setBenchUi({ distMetric: x.ci }), tip: 'Show ' + x.label, c: x.vals[0].c, color: tone(x.vals[0].c) }))
    : sel.vals
        .slice()
        .sort((a, b) => b.c - a.c)
        .map((x, i) => ({ n: String(i + 1), key: x.r.id, name: x.r.name, v: ord(x.c), onClick: openB(x.r.id), tip: 'Open ' + x.r.name + ' on Portfolio', c: x.c, color: pc(x.c), vColor: tone(x.c) }));
  const dragCol = one ? '0px' : '150px';

  return (
    <BenchFrame label="Distribution">
      {(ctx.op ? !!ctx.board?.data : !!ctx.fac) && (
        <div data-screen-label="Performance · Distribution" data-testid="distribution" style={sx('border-top:1px solid rgba(34,39,46,.14);padding:12px 18px 10px;margin-top:12px;min-width:0;')}>
          <div style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:4px;min-width:0;')}>
            <span style={sx('font-size:16px;font-weight:700;letter-spacing:-.01em;')}>
              <span>{one ? 'Your building on each of the ' + COLS.length + ' metrics' : COLS.length + ' metrics × ' + dsc.length + ' facilities'}</span>
            </span>
            <span style={sx('font-size:14px;color:#54606C;')}>
              <span>{one ? 'against ' + peerLabel + ', one axis for every metric' : 'each dot is one building, percentile against ' + peerLabel}</span>
            </span>
            <span style={sx('flex:1;')} />
            <span style={sx('display:flex;align-items:center;gap:12px;flex-wrap:wrap;font-size:14px;color:#54606C;')}>
              <span style={sx('display:inline-flex;align-items:center;gap:5px;')}>
                <i style={sx('display:inline-block;width:18px;height:7px;border-radius:3px;background:rgba(47,138,111,.22);')} />
                <span>{one ? 'peer p10–p90' : 'portfolio p10–p90'}</span>
              </span>
              <span style={sx('display:inline-flex;align-items:center;gap:5px;')}>
                <i style={sx('display:inline-block;width:2px;height:11px;background:rgba(34,39,46,.5);')} />
                typical
              </span>
              <span style={sx(one ? 'display:none;' : 'display:inline-flex;align-items:center;gap:5px;')}>
                <i style={sx('display:inline-block;width:0;height:0;border-left:4px solid transparent;border-right:4px solid transparent;border-top:5px solid #5B6B7C;')} />
                mean
              </span>
              <span
                title={
                  one
                    ? 'Every strip is a 0–100 percentile axis against ' + peerLabel + '. The band is the peer p10–p90 and the line is typical for peers; the dot is your building. Metrics marked † are ranked against state peers per CMS methodology and do not follow the peer-tier selector.'
                    : 'Every strip is a 0–100 percentile axis. The band is p10–p90 across the ' + dsc.length + ' buildings in view and the line is their typical figure. A caret marks the mean when it differs from the typical figure: left of the line means a few weak buildings drag the average down. A spread needs at least 3 buildings. Metrics marked † are state-relative per CMS methodology.'
                }
                style={sx('display:inline-flex;align-items:center;justify-content:center;width:17px;height:17px;border-radius:50%;border:1px solid rgba(34,39,46,.3);font-size:14px;font-weight:700;color:#3C454F;cursor:help;')}
              >
                i
              </span>
            </span>
          </div>
          <div style={sx('display:flex;flex-wrap:wrap;gap:12px 22px;margin-top:8px;min-width:0;')}>
            <div style={sx('flex:1.75 1 560px;min-width:0;overflow-x:auto;')}>
              <div style={sx('display:grid;grid-template-columns:138px minmax(220px,1fr) 104px ' + dragCol + ';gap:0 12px;align-items:center;padding:0 0 4px;font-size:14px;letter-spacing:.1em;text-transform:uppercase;font-weight:600;color:#54606C;min-width:560px;')}>
                <span>Metric</span>
                <span style={sx('position:relative;height:13px;')}>
                  <span style={sx('position:absolute;left:0;')}>0</span>
                  <span style={sx('position:absolute;left:25%;transform:translateX(-50%);')}>25</span>
                  <span style={sx('position:absolute;left:50%;transform:translateX(-50%);')}>50</span>
                  <span style={sx('position:absolute;left:75%;transform:translateX(-50%);')}>75</span>
                  <span style={sx('position:absolute;right:0;')}>100</span>
                </span>
                <span>
                  <span>{one ? 'Percentile' : 'Skew'}</span>
                </span>
                <span>
                  <span>{one ? '' : 'Biggest drag'}</span>
                </span>
              </div>
              {rows.map(({ st, on, thin, skewN, dots }) => (
                <div
                  key={st.label}
                  data-testid="dist-row"
                  style={sx(
                    'display:grid;grid-template-columns:138px minmax(220px,1fr) 104px ' + dragCol + ';gap:0 12px;align-items:center;padding:' + (one ? '4px' : '7px') + ' 0;border-top:1px solid rgba(34,39,46,.07);min-width:560px;' +
                      (on ? 'background:rgba(79,138,214,.06);' : '') + 'animation:riseIn .28s cubic-bezier(.22,.61,.36,1);animation-delay:' + Math.min(320, st.ci * 20) + 'ms;',
                  )}
                >
                  <span
                    role="button"
                    tabIndex={0}
                    aria-pressed={on}
                    onClick={() => setBenchUi({ distMetric: st.ci })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') setBenchUi({ distMetric: st.ci });
                    }}
                    title={'Rank ' + (one ? 'your building' : 'the buildings') + ' on ' + st.label}
                    style={sx('font-size:16px;cursor:pointer;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;' + (on ? 'font-weight:800;color:#2F5C97;' : 'font-weight:600;color:#22272E;'))}
                  >
                    <span>{st.label + dagger(st.ci)}</span>
                  </span>
                  <span style={sx('position:relative;display:block;height:20px;min-width:0;')}>
                    <i style={sx('position:absolute;left:0;right:0;top:8px;height:4px;border-radius:2px;background:rgba(34,39,46,.07);')} />
                    <i style={sx(thin ? 'display:none;' : 'position:absolute;top:6px;height:8px;border-radius:4px;background:rgba(47,138,111,.22);left:' + st.p10 + '%;width:' + Math.max(2, (st.p90 ?? 0) - (st.p10 ?? 0)) + '%;')} />
                    <i style={sx(thin ? 'display:none;' : 'position:absolute;top:2px;height:16px;width:2px;margin-left:-1px;background:rgba(34,39,46,.5);left:' + st.p50 + '%;')} />
                    <i style={sx(skewN === 0 ? 'display:none;' : 'position:absolute;top:-3px;width:0;height:0;margin-left:-4px;border-left:4px solid transparent;border-right:4px solid transparent;border-top:5px solid #5B6B7C;left:' + st.mean + '%;')} />
                    {dots.map((d) => (
                      <span key={d.key} onClick={d.onClick} title={d.tip} style={sx(d.style)} />
                    ))}
                  </span>
                  <span style={sx('font-size:14px;font-weight:600;white-space:nowrap;font-variant-numeric:tabular-nums;color:' + (one && st.vals.length ? tone(st.vals[0].c) : skewN < 0 ? '#B3261E' : skewN > 0 ? '#1F6B52' : '#54606C') + ';')}>
                    <span>{thin ? (one ? '' : st.vals.length + ' reported') : one ? ord(st.vals[0].c) + ' pctl' : skewN === 0 ? 'symmetric' : 'mean ' + Math.abs(skewN) + (skewN < 0 ? ' below' : ' above')}</span>
                  </span>
                  <span
                    onClick={one || !st.low ? undefined : openB(st.low.r.id)}
                    title={one || !st.low ? '' : 'Lowest on ' + st.label + ': ' + st.low.r.name + ' at ' + ord(st.low.c)}
                    style={sx(one ? 'display:none;' : 'font-size:14px;color:#3C454F;cursor:pointer;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;')}
                  >
                    <span>{one || !st.low ? '' : st.low.r.name}</span>
                  </span>
                </div>
              ))}
            </div>
            <div style={sx('flex:1 1 250px;border-left:1px solid rgba(34,39,46,.1);padding-left:18px;min-width:0;')} data-testid="dist-rank">
              <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:2px;')}>
                <span style={sx('font-size:14px;font-weight:700;letter-spacing:.11em;text-transform:uppercase;color:#54606C;')}>
                  <span>{one ? 'Lowest first' : 'Ranked on'}</span>
                </span>
              </div>
              <div style={sx('font-size:17px;font-weight:700;letter-spacing:-.01em;margin-top:3px;')}>
                <span>{one ? 'Where your building sits' : sel.label}</span>
              </div>
              <div style={sx('font-size:14px;color:#54606C;margin-top:2px;line-height:1.4;')}>
                <span>{one ? 'Click a metric to highlight it' : 'p10 ' + sel.p10 + ' · p50 ' + sel.p50 + ' · p90 ' + sel.p90 + ' · click a metric on the left to re-rank'}</span>
              </div>
              <div style={sx('display:grid;gap:1px;margin-top:8px;')}>
                {rank.map((k) => (
                  <div
                    key={k.key}
                    role="button"
                    tabIndex={0}
                    onClick={k.onClick}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') k.onClick();
                    }}
                    title={k.tip}
                    style={sx('display:grid;grid-template-columns:18px minmax(0,1fr) 72px 40px;gap:8px;align-items:center;padding:5px 6px;margin:0 -6px;border-radius:6px;cursor:pointer;animation:riseIn .28s cubic-bezier(.22,.61,.36,1);')}
                  >
                    <span style={sx('font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;')}>
                      <span>{k.n}</span>
                    </span>
                    <span style={sx('font-size:16px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;')}>
                      <span>{k.name}</span>
                    </span>
                    <span style={sx('display:block;height:6px;border-radius:3px;background:rgba(34,39,46,.07);overflow:hidden;')}>
                      <i style={sx('display:block;height:100%;border-radius:3px;background:' + k.color + ';width:' + k.c + '%;')} />
                    </span>
                    <span style={sx('font-size:14px;font-weight:700;text-align:right;font-variant-numeric:tabular-nums;color:' + tone(k.c) + ';')}>
                      <span>{k.v}</span>
                    </span>
                  </div>
                ))}
              </div>
              <div style={sx('font-size:14px;color:#54606C;line-height:1.45;margin-top:8px;padding-top:7px;border-top:1px solid rgba(34,39,46,.09);text-wrap:pretty;')}>
                <span>{one ? 'Peer percentiles only. Other facilities are never named on this view.' : 'Click a building to open it on Portfolio. Masked view: no resident data here.'}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </BenchFrame>
  );
}
